import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface TextFieldProps {
  id: string;
  content: string;
  isActive?: boolean;
  onUpdate: (id: string, content: string) => void;
  onDelete: (id: string) => void;
  onFocus: (id: string) => void;
  onEnter: (id: string) => void;
  onBackspace: (id: string) => void;
  onArrowUp: (id: string) => void;
  onArrowDown: (id: string) => void;
  onCommand: (id: string, command: string) => void;
  className?: string;
}

export const TextField: React.FC<TextFieldProps> = ({
  id,
  content,
  isActive = false,
  onUpdate,
  onDelete,
  onFocus,
  onEnter,
  onBackspace,
  onArrowUp,
  onArrowDown,
  onCommand,
  className
}) => {
  const [localContent, setLocalContent] = useState(content);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Update local content when prop changes
  useEffect(() => {
    setLocalContent(content);
  }, [content]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [localContent]);

  // Focus textarea when block becomes active
  useEffect(() => {
    if (isActive && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isActive]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setLocalContent(newContent);
    onUpdate(id, newContent);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onEnter(id);
    } else if (e.key === 'Backspace' && localContent === '') {
      e.preventDefault();
      onBackspace(id);
    } else if (e.key === 'ArrowUp' && e.currentTarget.selectionStart === 0) {
      e.preventDefault();
      onArrowUp(id);
    } else if (e.key === 'ArrowDown' && e.currentTarget.selectionEnd === localContent.length) {
      e.preventDefault();
      onArrowDown(id);
    } else if (e.key === '/' && localContent.endsWith('/')) {
      onCommand(id, '/');
    }
  };

  const handleFocus = () => {
    onFocus(id);
  };

  const handleBlur = () => {
    // Debounce the update to avoid too many calls
    setTimeout(() => {
      onUpdate(id, localContent);
    }, 100);
  };

  return (
    <div className={cn(
      "relative group min-h-[2.5rem] rounded-lg transition-all duration-200",
      isActive 
        ? "bg-white shadow-md border-2 border-blue-500" 
        : "bg-gray-50 hover:bg-gray-100 border-2 border-transparent",
      className
    )}>
      <textarea
        ref={textareaRef}
        value={localContent}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder="Type '/' for commands..."
        className={cn(
          "w-full min-h-[2.5rem] px-4 py-2 bg-transparent border-none outline-none resize-none",
          "text-gray-900 placeholder-gray-500",
          "focus:ring-0 focus:outline-none"
        )}
        style={{ 
          fontFamily: 'inherit',
          fontSize: '1rem',
          lineHeight: '1.5'
        }}
      />
      
      {/* Delete button - only show on hover or when active */}
      {(isActive || content.length > 0) && (
        <button
          onClick={() => onDelete(id)}
          className={cn(
            "absolute top-2 right-2 p-1 rounded-md transition-all duration-200",
            "opacity-0 group-hover:opacity-100",
            "text-gray-400 hover:text-red-500 hover:bg-red-50",
            "focus:opacity-100 focus:outline-none"
          )}
          title="Delete block"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
        </button>
      )}
    </div>
  );
}; 